import { useCallback, useMemo, memo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function AboutUs({ aboutUsImage }) {
  const { t } = useTranslation();

  const aboutUs = useMemo(
    () => t("components.home.aboutUs", { returnObjects: true }),
    [t]
  );
  const renderDescription = useCallback((descArray) => {
    return descArray.map((item, index) => {
      if (typeof item === "string") {
        return <span key={index}>{item}</span>;
      }
      if (item.emphasis) {
        return (
          <span
            key={index}
            className="text-[#009fd6] font-medium text-base md:text-lg lg:text-xl xl:text-2xl"
          >
            {item.text}
          </span>
        );
      }
      return <span key={index}>{item.text}</span>;
    });
  }, []);

  const fadeInVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    }),
    []
  );

  return (
    <section className="py-12 bg-black overflow-x-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-12">
        <div className="mb-8 flex flex-col md:flex-row items-start md:space-x-12 mt-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full text-center md:w-1/3 md:mt-6 xl:mt-0"
          >
            <motion.h2
              variants={fadeInVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl lg:text-3xl font-bold mb-20 md:mb-40 lg:mb-30 xl:mb-20 text-white"
            >
              {aboutUs.title} -
            </motion.h2>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 300 }}
              className="hidden md:block"
            >
              <img
                loading="lazy"
                src={aboutUsImage}
                alt={aboutUs.title}
                className="rounded-lg shadow-lg w-full max-w-full transition-transform duration-300 hover:scale-105"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:w-2/3 md:pl-8 mt-4 md:mt-0"
          >
            <motion.p
              variants={fadeInVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-md lg:text-lg mb-16 text-gray-300 leading-relaxed"
            >
              {renderDescription(aboutUs.description)}
            </motion.p>

            <div className="flex flex-col md:flex-row justify-between mt-10 md:space-x-12 lg:max-w-3xl lg:mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="md:w-1/2 mb-8 md:mb-0"
              >
                <h3 className="text-md md:text-xl font-bold mb-6 text-gray-200 leading-snug">
                  {aboutUs.mission.title}
                </h3>
                <div className="w-full h-[1px] bg-gray-700 mb-6"></div>
                <p className="text-md lg:text-lg text-gray-300 leading-relaxed">
                  {aboutUs.mission.description}
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="md:w-1/2"
              >
                <h3 className="text-md md:text-xl font-bold mb-6 text-gray-200 leading-snug">
                  {aboutUs.vision.title}
                </h3>
                <div className="w-full h-[1px] bg-gray-700 mb-6"></div>
                <p className="text-md lg:text-lg text-gray-300 leading-relaxed">
                  {aboutUs.vision.description}
                </p>
              </motion.div>
            </div>

            <motion.div
              variants={fadeInVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.3 }}
              className="flex items-center mt-12 lg:max-w-3xl lg:mx-auto"
            >
              <p className="text-md lg:text-lg text-gray-300 mr-auto leading-relaxed">
                {aboutUs.learnmore}
              </p>
              <Link
                to="/about"
                aria-label={aboutUs.learnmore}
                className="flex items-center"
              >
                <motion.div
                  className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 flex items-center justify-center cursor-pointer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white transform rotate-45"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 11l5-5m0 0l5 5m-5-5v12"
                    />
                  </svg>
                </motion.div>
              </Link>
            </motion.div>
            <div className="w-full h-[1px] bg-gray-700 mt-4 lg:max-w-3xl lg:mx-auto"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

AboutUs.propTypes = {
  aboutUsImage: PropTypes.string.isRequired,
};

export default memo(AboutUs);
